import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  endAt,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  startAt,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { deleteObject, listAll, ref } from "firebase/storage";
import { distanceBetween, geohashQueryBounds } from "geofire-common";
import { toast } from "react-toastify";

import { DisplayGigsState } from "../constants/DisplayGig";
import {
  NEARBY_GIG_DISTANCE_LIMIT,
  NUMBER_OF_NEARBY_GIG_LIMIT,
  POTENTIAL_FALSE_POSITIVES,
} from "../constants/General";
import { PrivateGigConstants } from "../constants/gigConstants";
import {
  chefPublicProfileGigsState,
  clientPublicProfileGigsState,
} from "../constants/ProfileConstant";
import ProposalState from "../constants/ProposalState";
import UserType from "../constants/UserType";
import { canEditGig, canEditProposal } from "../helper/editPermissions";
import {
  calculateFinalGrossTotal,
  calculateInitialGrossTotal,
} from "../helper/finance";
import getIsoStringFromTimestamp from "../helper/getIsoStringFromTimestamp";
import { isItDev } from "../helper/isItDev";
import removeUndefinedFields from "../helper/removeUndefinedFields";
import { Gig, GigProposal, UserProposedGig } from "../interfaces/Gig";
import { GoogleMapResultData } from "../interfaces/GoogleMap";
import { FIREBASE_COLLECTIONS } from "../types/enums";
import { PaginationData } from "../types/types";
import { fetchPostJSON } from "../utils/api-helpers";
import { sendEmailRequest } from "./email";
import { auth, db, storage } from "./firebase";
import { createUserLog } from "./logs";
import { updateGeoHash } from "./user";

const PRIVATE_GIG_INVITE_TEMPLATE_ID = "GPI1H";

export interface ProposalDetailsData {
  gigDetails: UserProposedGig;
  proposalDetails: GigProposal;
}

const canViewGig = (gig: Partial<Gig>, viewerUid?: string | null) => {
  if (!gig?.isPrivate) {
    return true;
  }
  if (!viewerUid) {
    return false;
  }
  if (gig.ownerUid && gig.ownerUid === viewerUid) {
    return true;
  }
  return (gig.invitedChefUids ?? []).includes(viewerUid);
};

/**
 * The Chef Submits a Proposal to the Gig Posted by the Client
 *
 *  */
const submitGigProposal = async (
  gigProposalData: GigProposal,
  gigData: UserProposedGig
) => {
  if (!gigProposalData) {
    return;
  }
  if (
    gigProposalData &&
    gigProposalData.id &&
    gigProposalData.proposalState &&
    gigProposalData.proposalState !== ProposalState.DRAFT
  ) {
    toast.error("You have already submitted a proposal for this gig");
    return;
  }
  let proposalId = gigProposalData.id;
  if (gigProposalData.id) {
    // The proposal was in a Draft state and is now in Open Bid state
    const docRef = doc(db, FIREBASE_COLLECTIONS.proposals, gigProposalData.id);
    await updateDoc(docRef, {
      ...gigProposalData,

      proposalState: ProposalState.OPEN_BID,
      createdAt: gigProposalData?.createdAt
        ? Timestamp.fromDate(new Date(gigProposalData.createdAt))
        : Timestamp.fromDate(new Date()),

      updatedAt: Timestamp.fromDate(new Date()),
    });
  } else {
    const newProposalRef = await addDoc(
      collection(db, FIREBASE_COLLECTIONS.proposals),
      {
        ...gigProposalData,
        gigId: gigData.gigId,
        clientUid: gigData.ownerUid,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        proposalState: ProposalState.OPEN_BID,
      }
    );
    proposalId = newProposalRef.id;
  }
  if (gigData) {
    const clientNotAcceptedProposal =
      [ProposalState.NEW_GIG, ProposalState.OPEN_BID].findIndex(
        (state) => state === gigData.gigState
      ) >= 0;
    if (clientNotAcceptedProposal) {
      await updateGigState(gigData.gigId, ProposalState.OPEN_BID);
    }
  }

  // Log proposal submission for chef
  if (gigProposalData.chefUid && proposalId) {
    await createUserLog(
      gigProposalData.chefUid,
      "Proposal Submitted",
      `Chef submitted proposal for gig ${gigData.gigId}`,
      { proposalId, gigId: gigData.gigId, clientUid: gigData.ownerUid },
      "Proposal Submission"
    );
  }
  // Send P3H to chef confirming proposal submission
  await sendEmailRequest(gigProposalData.chefUid, "P3H", [
    { key: "gigTitle", value: gigData?.title || "" },
    {
      key: "clientName",
      value:
        [gigData?.ownerFirstName, gigData?.ownerLastName]
          .filter(Boolean)
          .join(" ") || "Client",
    },
  ]);
};

const updateGigState = async (gigId: string, newState: ProposalState) => {
  const docRef = doc(db, `gigs/${gigId}`);
  await updateDoc(docRef, {
    gigState: newState,
  });
};

const updateGigProposal = async (gigProposalData: GigProposal) => {
  if (!gigProposalData.id) {
    toast.error("Something went wrong please try again later");
    await createUserLog(
      gigProposalData.clientUid,
      "Error",
      "Error while adding settings for proposal",
      {},
      "Proposal Error"
    );
    return;
  }
  const docRef = doc(db, FIREBASE_COLLECTIONS.proposals, gigProposalData.id);
  const latestSnap = await getDoc(docRef);
  const latestData = latestSnap.data() as GigProposal | undefined;
  if (!canEditProposal(latestData)) {
    toast.error("You can't edit this proposal after it has been selected.");
    return;
  }
  await updateDoc(docRef, {
    ...gigProposalData,
    createdAt: Timestamp.fromDate(new Date(gigProposalData?.createdAt)),
    updatedAt: Timestamp.fromDate(new Date()),
  });
};

// TODO How to handle cancel proposal
const handleCancelProposalAfterSubmit = async (
  gigId: string,
  gigProposalData: GigProposal
) => {
  if (gigProposalData.id) {
    const docRef = doc(db, FIREBASE_COLLECTIONS.proposals, gigProposalData.id);
    let proposalState = ProposalState.CANCELED_BID;
    if (gigProposalData.proposalState === ProposalState.DEPOSIT_PAID) {
      await fetchPostJSON("/api/users/cancel-proposal", {
        userType: UserType.Chef,
        gigProposalData,
      });
      return;
    }
    await updateDoc(docRef, {
      ...gigProposalData,
      proposalState,
      createdAt: Timestamp.fromDate(new Date(gigProposalData?.createdAt)),

      updatedAt: Timestamp.fromDate(new Date()),
    });
    return;
  }
};

const saveGigProposal = async (gigProposalData: GigProposal, gigId: string) => {
  let proposalData = { ...gigProposalData };

  const proposalRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("chefUid", "==", gigProposalData.chefUid),
    where("gigId", "==", gigProposalData.gigId)
  );
  const proposalSnap = await getDocs(proposalRef);
  if (!proposalSnap.empty) {
    const docRef = proposalSnap.docs[0].ref;
    const docData = proposalSnap.docs[0].data() as GigProposal;
    if (!canEditProposal(docData)) {
      toast.error("You can't edit this proposal after it has been selected.");
      return;
    }
    proposalData.createdAt = docData.createdAt;

    proposalData.updatedAt = serverTimestamp();
    await updateDoc(docRef, {
      ...proposalData,
    });
    const updatedProposal = await fetchGigProposalById(proposalSnap.docs[0].id);
    if (!updatedProposal) {
      return;
    }
    proposalData = updatedProposal;
    proposalData.id = docRef.id;
  } else {
    proposalData.createdAt = serverTimestamp();
    proposalData.updatedAt = serverTimestamp();
    proposalData.proposalState = ProposalState.DRAFT;
    const newProposal = await addDoc(
      collection(db, FIREBASE_COLLECTIONS.proposals),
      {
        ...proposalData,
      }
    );
    const newProposalData = await fetchGigProposalById(newProposal.id);
    if (!newProposalData) {
      return;
    }
    proposalData = newProposalData;
    proposalData.id = newProposal.id;
  }
  return proposalData;
};

const fetchGigProposalById = async (proposalId: string) => {
  try {
    const proposalRef = await getDoc(
      doc(db, `${FIREBASE_COLLECTIONS.proposals}/${proposalId}`)
    );
    if (!proposalRef.exists()) {
      isItDev() && toast.error("Something went wrong");
      return;
    }
    const data = proposalRef.data();
    const result = data;
    result.id = proposalRef.id;
    if (data.proposalHistory?.depositPaymentTime) {
      data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.depositPaymentTime
      );
    }
    if (data.proposalHistory?.gigCompletionTime) {
      data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigCompletionTime
      );
    }
    if (data.proposalHistory?.gigPaymentTime) {
      data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigPaymentTime
      );
    }

    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.updatedAt =
      data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);

    return result as GigProposal;
  } catch (error) {
    isItDev() && toast.error("Something went wrong");
    if (auth.currentUser?.uid) {
      await createUserLog(
        auth.currentUser.uid,
        "Error",
        "Error while fetching draft proposal",
        {},
        "Proposal Error"
      );
    }
    return;
  }
};

const handleCancelProposal = async (chefUid: string, gigId: string) => {
  try {
    const proposalsRef = query(
      collection(db, FIREBASE_COLLECTIONS.proposals),
      where("chefUid", "==", chefUid),
      where("gigId", "==", gigId)
    );
    const proposalsSnap = await getDocs(proposalsRef);
    if (!proposalsSnap.empty) {
      await deleteDoc(proposalsSnap.docs[0].ref);
    }
    const storageRef = ref(storage, `gigs/${gigId}/proposal/${chefUid}/images`);
    const listResults = await listAll(storageRef);
    const promises = listResults.items.map((item) => {
      return deleteObject(item);
    });
    await Promise.all(promises);
  } catch (error) {
    console.log(error);
  }
};

const fetchDraftProposal = async (chefUid: string, gigId: string) => {
  let result: Partial<GigProposal> = { gigId };
  try {
    const proposalRef = query(
      collection(db, FIREBASE_COLLECTIONS.proposals),
      where("chefUid", "==", chefUid),
      where("gigId", "==", gigId),
      where("proposalState", "==", ProposalState.DRAFT)
    );
    const proposalSnap = await getDocs(proposalRef);
    if (!proposalSnap.empty) {
      const data = proposalSnap.docs[0].data() as any;
      result = data;
      result.id = proposalSnap.docs[0].id;
      if (data.proposalHistory?.depositPaymentTime) {
        data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
          data.proposalHistory.depositPaymentTime
        );
      }
      if (data.proposalHistory?.gigCompletionTime) {
        data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
          data.proposalHistory.gigCompletionTime
        );
      }
      if (data.proposalHistory?.gigPaymentTime) {
        data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
          data.proposalHistory.gigPaymentTime
        );
      }
      data.createdAt =
        data.createdAt && getIsoStringFromTimestamp(data.createdAt);
      data.updatedAt =
        data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);
    } else {
      toast.info("Proposal draft not found");
    }
  } catch (error) {
    isItDev() && toast.error("Something went wrong");
    await createUserLog(
      chefUid,
      "Error",
      "Error while fetching draft proposal",
      {},
      "Proposal Error"
    );
  }
  return result;
};

const fetchLikedGigsForChef = async (
  chefUid: string
): Promise<ProposalDetailsData[]> => {
  const gigQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.gigs}`),
    where("likedBy", "array-contains", chefUid)
  );
  const gigsQuerySnapshot = await getDocs(gigQuery);
  const likedGigs = (
    await Promise.all(
      gigsQuerySnapshot.docs.map(async (gig) => {
        const proposalQuery = query(
          collection(db, `${FIREBASE_COLLECTIONS.gigs}`),
          where("gigId", "==", gig.id),
          where("chefUId", "==", chefUid)
        );
        const proposalDocs = await getDocs(proposalQuery);
        let proposalDetails = null;

        if (proposalDocs.docs.length > 0) {
          proposalDetails = proposalDocs.docs[0].data();
          if (
            proposalDetails.proposalHistory &&
            proposalDetails.proposalHistory.depositPaymentTime
          ) {
            proposalDetails.proposalHistory.depositPaymentTime =
              proposalDetails.proposalHistory.depositPaymentTime
                .toDate()
                .toISOString();
          }
          if (
            proposalDetails.proposalHistory &&
            proposalDetails.proposalHistory.gigCompletionTime
          ) {
            proposalDetails.proposalHistory.gigCompletionTime =
              proposalDetails.proposalHistory.gigCompletionTime
                .toDate()
                .toISOString();
          }
          if (
            proposalDetails.proposalHistory &&
            proposalDetails.proposalHistory.gigPaymentTime
          ) {
            proposalDetails.proposalHistory.gigPaymentTime =
              proposalDetails.proposalHistory.gigPaymentTime
                .toDate()
                .toISOString();
          }

          proposalDetails.createdAt = proposalDetails.createdAt
            .toDate()
            .toISOString();
          proposalDetails.updatedAt = proposalDetails.updatedAt
            .toDate()
            .toISOString();
        }
        const gigDetails = gig.data();
        gigDetails.gigId = gig.id;
        if (!canViewGig(gigDetails, chefUid)) {
          return null;
        }
        gigDetails.createdAt =
          gigDetails.createdAt &&
          getIsoStringFromTimestamp(gigDetails.createdAt);
        gigDetails.finishedAndPaidAt =
          gigDetails.finishedAndPaidAt &&
          getIsoStringFromTimestamp(gigDetails.finishedAndPaidAt);
        gigDetails.depositPaidAt =
          gigDetails.depositPaidAt &&
          getIsoStringFromTimestamp(gigDetails.depositPaidAt);
        return {
          gigDetails: gigDetails as UserProposedGig,
          proposalDetails: proposalDetails as GigProposal,
        };
      })
    )
  ).filter(Boolean) as ProposalDetailsData[];

  return likedGigs;
};

const fetchGigProposal = async (chefUid: string, gigId: string) => {
  let result: Partial<GigProposal> = { gigId };
  try {
    const proposalRef = query(
      collection(db, FIREBASE_COLLECTIONS.proposals),
      where("chefUid", "==", chefUid),
      where("gigId", "==", gigId)
    );
    const proposalSnap = await getDocs(proposalRef);
    if (!proposalSnap.empty) {
      const data = proposalSnap.docs[0].data() as any;
      result = data;
      result.id = proposalSnap.docs[0].id;
      if (data.proposalHistory?.depositPaymentTime) {
        data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
          data.proposalHistory.depositPaymentTime
        );
      }
      if (data.proposalHistory?.gigCompletionTime) {
        data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
          data.proposalHistory.gigCompletionTime
        );
      }
      if (data.proposalHistory?.gigPaymentTime) {
        data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
          data.proposalHistory.gigPaymentTime
        );
      }

      data.createdAt =
        data.createdAt && getIsoStringFromTimestamp(data.createdAt);
      data.updatedAt =
        data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);
    }
  } catch (error) {
    console.log(error);
  }
  return result;
};

const fetchAcceptedProposalsForClientWithState = async (
  clientUid: string,
  state: ProposalState
) => {
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("clientUid", "==", clientUid),
    where("proposalState", "==", state)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.createdAt);
      proposalDetails.updatedAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.updatedAt);

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (!gigDoc.exists()) {
        throw new Error("Gig doesn't exist");
      }
      const gigDetails = gigDoc.data();
      gigDetails.gigId = gigDoc.id;
      gigDetails.createdAt =
        gigDetails.createdAt && getIsoStringFromTimestamp(gigDetails.createdAt);
      return {
        gigDetails: gigDetails as UserProposedGig,
        proposalDetails: proposalDetails as GigProposal,
      };
    })
  );
  return proposedGigs;
};
const fetchAcceptedProposalsForClientForStates = async (
  clientUid: string,
  states: ProposalState[]
) => {
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("clientUid", "==", clientUid),
    where("proposalState", "in", states)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.createdAt);
      proposalDetails.updatedAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.updatedAt);

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (!gigDoc.exists()) {
        throw new Error("Gig doesn't exist");
      }
      const gigDetails = gigDoc.data();
      gigDetails.gigId = gigDoc.id;
      gigDetails.createdAt =
        gigDetails.createdAt && getIsoStringFromTimestamp(gigDetails.createdAt);
      return {
        gigDetails: gigDetails as UserProposedGig,
        proposalDetails: proposalDetails as GigProposal,
      };
    })
  );
  return proposedGigs;
};

const fetchProposedGigsWithState = async (
  chefUid: string,
  state: ProposalState
) => {
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("chefUid", "==", chefUid),
    where("proposalState", "==", state)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.createdAt);
      proposalDetails.updatedAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.updatedAt);

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (gigDoc.exists()) {
        const gigDetails = gigDoc.data();
        gigDetails.gigId = gigDoc.id;
        if (!canViewGig(gigDetails, chefUid)) {
          return null;
        }
        gigDetails.createdAt =
          gigDetails.createdAt &&
          getIsoStringFromTimestamp(gigDetails.createdAt);
        return {
          gigDetails: gigDetails as UserProposedGig,
          proposalDetails: proposalDetails as GigProposal,
        };
      } else {
        throw new Error("Gig doesn't exist");
      }
    })
  );
  return proposedGigs.filter(Boolean) as {
    gigDetails: UserProposedGig;
    proposalDetails: GigProposal;
  }[];
};
const fetchProposedGigsForMultipleStates = async (
  chefUid: string,
  states: ProposalState[]
) => {
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("chefUid", "==", chefUid),
    where("proposalState", "in", states)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.createdAt);
      proposalDetails.updatedAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.updatedAt);

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (gigDoc.exists()) {
        const gigDetails = gigDoc.data();
        gigDetails.gigId = gigDoc.id;
        gigDetails.createdAt =
          gigDetails.createdAt &&
          getIsoStringFromTimestamp(gigDetails.createdAt);
        return {
          gigDetails: gigDetails as UserProposedGig,
          proposalDetails: proposalDetails as GigProposal,
        };
      } else {
        throw new Error("Gig doesn't exist");
      }
    })
  );
  return proposedGigs;
};
const fetchLikedProposedGigs = async (
  chefUid: string
): Promise<
  {
    gigDetails: UserProposedGig;
    proposalDetails: GigProposal;
  }[]
> => {
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("chefUid", "==", chefUid)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt = getIsoStringFromTimestamp(
        proposalDetails.createdAt
      );
      proposalDetails.updatedAt = getIsoStringFromTimestamp(
        proposalDetails.updatedAt
      );

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (gigDoc.exists()) {
        const gigDetails = gigDoc.data();
        gigDetails.gigId = gigDoc.id;
        gigDetails.createdAt = getIsoStringFromTimestamp(gigDetails.createdAt);
        return {
          gigDetails: gigDetails as UserProposedGig,
          proposalDetails: proposalDetails as GigProposal,
        };
      } else {
        throw new Error("Gig doesn't exist");
      }
    })
  );
  return proposedGigs;
};
const fetchProposedGigs = async (
  chefUid: string
): Promise<
  {
    gigDetails: UserProposedGig;
    proposalDetails: GigProposal;
  }[]
> => {
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("chefUid", "==", chefUid)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.createdAt);
      proposalDetails.updatedAt =
        proposalDetails.updatedAt &&
        getIsoStringFromTimestamp(proposalDetails.updatedAt);

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (gigDoc.exists()) {
        const gigDetails = gigDoc.data();
        gigDetails.gigId = gigDoc.id;
        gigDetails.createdAt =
          gigDetails.createdAt &&
          getIsoStringFromTimestamp(gigDetails.createdAt);
        return {
          gigDetails: gigDetails as UserProposedGig,
          proposalDetails: proposalDetails as GigProposal,
        };
      } else {
        throw new Error("Gig doesn't exist");
      }
    })
  );
  return proposedGigs;
};

const fetchGigsDataByUids = async (gigsUid: string[]) => {
  const gigsRef = collection(db, FIREBASE_COLLECTIONS.gigs);
  const q = query(gigsRef, where("uid", "in", gigsUid));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = doc.id;
    return data;
  });
};

const fetchClientGigs = async (userUid: string): Promise<UserProposedGig[]> => {
  const gigsRef = collection(db, FIREBASE_COLLECTIONS.gigs);
  const q = query(gigsRef, where("ownerUid", "==", userUid));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = doc.id;

    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.finishedAndPaidAt =
      data.finishedAndPaidAt &&
      getIsoStringFromTimestamp(data.finishedAndPaidAt);
    data.depositPaidAt =
      data.depositPaidAt && getIsoStringFromTimestamp(data.depositPaidAt);
    return data as UserProposedGig;
  });
};
const fetchClientGigsWithState = async (
  userUid: string,
  state: ProposalState | string
): Promise<UserProposedGig[]> => {
  const gigsRef = collection(db, FIREBASE_COLLECTIONS.gigs);
  const q = query(
    gigsRef,
    where("ownerUid", "==", userUid),
    where("gigState", "==", state)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = doc.id;

    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.finishedAndPaidAt =
      data.finishedAndPaidAt &&
      getIsoStringFromTimestamp(data.finishedAndPaidAt);
    data.depositPaidAt =
      data.depositPaidAt && getIsoStringFromTimestamp(data.depositPaidAt);
    return data as UserProposedGig;
  });
};

const fetchAllProposals = async (gigId: string): Promise<GigProposal[]> => {
  const allProposalRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("gigId", "==", gigId),
    where("proposalState", "!=", ProposalState.DRAFT)
  );
  const querySnapshot = await getDocs(allProposalRef);

  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = gigId;
    data.uid = doc.id;
    if (data.proposalHistory?.depositPaymentTime) {
      data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.depositPaymentTime
      );
    }
    if (data.proposalHistory?.gigCompletionTime) {
      data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigCompletionTime
      );
    }
    if (data.proposalHistory?.gigPaymentTime) {
      data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigPaymentTime
      );
    }
    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.updatedAt =
      data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);
    return data as GigProposal;
  });
};
const fetchAllProposalsWithState = async (
  gigId: string,
  state: ProposalState
): Promise<GigProposal[]> => {
  let newState = state;
  if (state === ProposalState.ACCEPTED_BOOKED) {
    newState = ProposalState.DEPOSIT_PAID;
  }
  const allProposalRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("gigId", "==", gigId),
    where("proposalState", "==", newState)
  );
  const querySnapshot = await getDocs(allProposalRef);

  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = gigId;
    data.uid = doc.id;
    if (data.proposalHistory?.depositPaymentTime) {
      data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.depositPaymentTime
      );
    }
    if (data.proposalHistory?.gigCompletionTime) {
      data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigCompletionTime
      );
    }
    if (data.proposalHistory?.gigPaymentTime) {
      data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigPaymentTime
      );
    }
    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.updatedAt =
      data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);
    return data as GigProposal;
  });
};

const countAllProposals = async (gigId: string): Promise<number> => {
  const coll = collection(db, "proposals");
  const q = query(coll, where("gigId", "==", gigId));
  const snapshot = await getCountFromServer(q);
  const { count } = snapshot.data();
  return count;
};

const hasSelectedProposals = async (gigId: string): Promise<boolean> => {
  try {
    const selectedStates = [
      ProposalState.PENDING,
      ProposalState.DEPOSIT_PAID,
      ProposalState.ACCEPTED_BOOKED,
    ];

    const allProposalRef = query(
      collection(db, FIREBASE_COLLECTIONS.proposals),
      where("gigId", "==", gigId),
      where("proposalState", "in", selectedStates)
    );
    const querySnapshot = await getDocs(allProposalRef);

    return querySnapshot.docs.length > 0;
  } catch (error) {
    console.error("Error checking for selected proposals:", error);
    return false;
  }
};

const fetchProposals = async (gigId: string) => {
  const allProposalRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("gigId", "==", gigId),
    where("proposalState", "in", [
      ProposalState.OPEN_BID,
      ProposalState.DEPOSIT_PAID,
      ProposalState.PENDING,
    ])
  );
  const querySnapshot = await getDocs(allProposalRef);

  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = gigId;
    data.uid = doc.id;
    if (data.proposalHistory?.depositPaymentTime) {
      data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.depositPaymentTime
      );
    }
    if (data.proposalHistory?.gigCompletionTime) {
      data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigCompletionTime
      );
    }
    if (data.proposalHistory?.gigPaymentTime) {
      data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigPaymentTime
      );
    }
    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.updatedAt =
      data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);
    return data;
  });
};

const fetchRejectedProposals = async (gigId: string) => {
  const queryRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("gigId", "==", gigId),
    where("proposalState", "==", ProposalState.DECLINED_BID)
  );
  const querySnapshot = await getDocs(queryRef);

  return querySnapshot.docs.map((doc) => {
    const data = doc.data();
    data.gigId = gigId;
    if (data.proposalHistory?.depositPaymentTime) {
      data.proposalHistory.depositPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.depositPaymentTime
      );
    }
    if (data.proposalHistory?.gigCompletionTime) {
      data.proposalHistory.gigCompletionTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigCompletionTime
      );
    }
    if (data.proposalHistory?.gigPaymentTime) {
      data.proposalHistory.gigPaymentTime = getIsoStringFromTimestamp(
        data.proposalHistory.gigPaymentTime
      );
    }
    data.createdAt =
      data.createdAt && getIsoStringFromTimestamp(data.createdAt);
    data.updatedAt =
      data.updatedAt && getIsoStringFromTimestamp(data.updatedAt);
    return data;
  });
};

// TODO: manage gig status after decline

const handleDeclinedGigProposal = async (gigProposalData: GigProposal) => {
  const proposalRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("chefUid", "==", gigProposalData.chefUid),
    where("gigId", "==", gigProposalData.gigId)
  );
  const proposalSnap = await getDocs(proposalRef);
  if (!proposalSnap.empty) {
    await updateDoc(proposalSnap.docs[0].ref, {
      proposalState: ProposalState.DECLINED_BID,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }
};

const handleCancelGigProposal = async (
  userType: UserType,
  gigProposalData: GigProposal
) => {
  const response = await fetchPostJSON("/api/users/cancel-proposal", {
    userType,
    gigProposalData,
  });
  return response;
};

const handleCancelGigProposalClientAtFault = async (
  userUid: string,
  userType: UserType,
  gigProposalData: GigProposal
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);
  const response = await fetchPostJSON("/api/users/clientAtFaultCancellation", {
    user: { uid: userUid, ...docSnap.data() },
    userType,
    gigProposalData,
  });
  return response;
};

/**
 * Handles the client cancellation of a gig proposal during the grace period.
 * Redirects requests to the API to do whats necessary
 */
const handleCancelGigProposalClientGracePeriodCancellation = async (
  userUid: string,
  userType: UserType,
  gigProposalData: GigProposal
) => {
  console.log("handleCancelGigProposalClientGracePeriodCancellation");
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);

  const response = await fetchPostJSON(
    "/api/users/clientGracePeriodCancellation",
    {
      user: { uid: userUid, ...docSnap.data() },
      userType,
      gigProposalData,
    }
  );
  return response;
};

const handleCancelGigProposalChefGracePeriodCancellation = async (
  userUid: string,
  userType: UserType,
  gigProposalData: GigProposal
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);

  const response = await fetchPostJSON(
    "/api/users/chefGracePeriodCancellation",
    {
      user: { uid: userUid, ...docSnap.data() },
      userType,
      gigProposalData,
    }
  );
  return response;
};

const handleCancelGigProposalChefAtFaultCancellation = async (
  userUid: string,
  userType: UserType,
  gigProposalData: GigProposal
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);

  const response = await fetchPostJSON("/api/users/chefAtFaultCancellation", {
    user: { uid: userUid, ...docSnap.data() },
    userType,
    gigProposalData,
  });
  return response;
};

/**
 * Redirects to stripe checkout page.
 * Uses the /api/users/book-proposal endpoint to create a payment intent
 * for the initial Deposit amount to be paid by the client.
 */
const handleBookGigProposal = async (
  gigProposalData: GigProposal,
  userUid: string,
  redirectUrl: string,
  tax_amount: number,
  service_charge: number,
  userType: UserType
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const response = await fetchPostJSON("/api/users/book-proposal", {
      amount: calculateInitialGrossTotal(
        gigProposalData.invoice?.total ?? 0,
        tax_amount,
        service_charge,
        userType
      ),
      user: { uid: userUid, ...docSnap.data() },
      gigProposalData,
      redirectUrl,
    });
    return response;
  }
};
/**
 * Redirects to stripe checkout page.
 * Uses the /api/users/complete-gig endpoint to create a payment intent
 * for the final Payment amount (75%) to be paid by the client.
 */
const handleBookGigProposalFinalPayment = async (
  gigProposalData: GigProposal,
  userUid: string,
  redirectUrl: string,
  tax_amount: number,
  service_charge: number,
  userType: UserType
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const response = await fetchPostJSON("/api/users/complete-gig", {
      amount: calculateFinalGrossTotal(
        gigProposalData.invoice?.total ?? 0,
        tax_amount,
        service_charge,
        userType
      ),
      user: { uid: userUid, ...docSnap.data() },
      gigProposalData,
      redirectUrl,
    });
    return response;
  }
};

const handleGigCompletionFlow = async (
  gigProposalData: GigProposal,
  userUid: string,
  redirectUrl: string
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);
  const response = await fetchPostJSON("/api/users/complete-gig", {
    user: { uid: userUid, ...docSnap.data() },
    gigProposalData,
    redirectUrl,
  });
  return response;
};

const fetchTotalProposals = async (gigId: string) => {
  const proposalsRef = query(
    collection(db, FIREBASE_COLLECTIONS.proposals),
    where("gigId", "==", gigId)
  );
  const snapshot = await getCountFromServer(proposalsRef);
  return snapshot.data().count;
};

const fetchGigsForPublicProfile = async (
  userUid: string,
  userType: UserType
) => {
  try {
    if (userType === UserType.Client) {
      let gigsData: Gig[];
      const snapShot = await getDocs(
        query(
          collection(db, "gigs"),
          orderBy("createdAt", "desc"),
          where("ownerUid", "==", userUid),
          where("gigState", "in", clientPublicProfileGigsState)
        )
      );
      gigsData = (
        await Promise.all(
          snapShot.docs.map(async (doc) => {
            const data = doc.data();
            data.gigId = doc.id;
            data.createdAt = data.createdAt?.toDate().toISOString();
            return data as Gig;
          })
        )
      ).filter((gig) => !gig.isPrivate);
      return gigsData;
    } else {
      let gigIds: string[];
      const snapShot = await getDocs(
        query(
          collection(db, "proposals"),
          orderBy("createdAt", "desc"),
          where("chefUid", "==", userUid),
          where("proposalState", "in", chefPublicProfileGigsState)
        )
      );
      gigIds = await Promise.all(
        snapShot.docs.map(async (doc) => {
          return doc.data().gigId;
        })
      );

      const gigsData = (
        await Promise.all(
          gigIds.map(async (gigId) => {
            let gigData: Gig;
            const docRef = doc(db, "gigs", gigId);
            const docSnap = await getDoc(docRef);
            gigData = docSnap.data() as Gig;
            return gigData;
          })
        )
      ).filter((gig) => !gig?.isPrivate);
      return gigsData;
    }
  } catch (error) {
    console.log(error);
  }
};

const fetchLikedGigsWithState = async (
  userId: string,
  state: ProposalState
): Promise<UserProposedGig[]> => {
  const docsRef = collection(db, "gigs");
  const q = query(
    docsRef,
    where("likedBy", "array-contains", userId),
    where("gigState", "==", state)
  );
  const docs = await getDocs(q);
  const likedGigs = (
    await Promise.all(
      docs.docs.map(async (doc) => {
        const result = doc.data();

        result.createdAt =
          result.createdAt && getIsoStringFromTimestamp(result.createdAt);
        result.gigId = doc.id;

        return result;
      })
    )
  ).filter((gig) => canViewGig(gig, userId)) as UserProposedGig[];
  return likedGigs;
};

const countLikedGigs = async (userId: string): Promise<number> => {
  const coll = collection(db, "gigs");
  const q = query(coll, where("likedBy", "array-contains", userId));
  const snapshot = await getCountFromServer(q);
  const { count } = snapshot.data();
  return count;
};
const countLikedGigsByState = async (
  userId: string,
  state: ProposalState
): Promise<number> => {
  const coll = collection(db, "gigs");
  const q = query(
    coll,
    where("likedBy", "array-contains", userId),
    where("gigState", "==", state)
  );
  const snapshot = await getCountFromServer(q);
  const { count } = snapshot.data();
  return count;
};
const countGigsByState = async (
  userId: string,
  state: ProposalState
): Promise<number> => {
  const coll = collection(db, "gigs");
  const q = query(
    coll,
    where("gigState", "==", state),
    where("ownerUid", "==", userId)
  );
  const snapshot = await getCountFromServer(q);
  const { count } = snapshot.data();
  return count;
};

const countProposalsForClientByState = async (
  userId: string,
  state: ProposalState
): Promise<number> => {
  const coll = collection(db, "gigs");
  const q = query(
    coll,
    where("ownerUid", "==", userId),
    where("gigState", "==", state)
  );
  let totalCount = 0;
  const gigs = await getDocs(q);
  await Promise.all(
    gigs.docs.map(async (gig) => {
      const proposalCol = collection(db, "proposals");
      let newState = state;
      if (state === ProposalState.ACCEPTED_BOOKED) {
        newState = ProposalState.DEPOSIT_PAID;
      }
      const proposalQ = query(
        proposalCol,
        where("gigId", "==", gig.id),
        where("proposalState", "==", newState)
      );
      const snapshot = await getCountFromServer(proposalQ);
      const { count } = snapshot.data();
      totalCount += count;
    })
  );
  return totalCount;
};

const countProposalsForChefByState = async (
  userId: string,
  state: ProposalState
): Promise<number> => {
  const coll = collection(db, "proposals");
  const q = query(
    coll,
    where("proposalState", "==", state),
    where("chefUid", "==", userId)
  );
  const snapshot = await getCountFromServer(q);
  const { count } = snapshot.data();
  return count;
};

const fetchLikedProposedGigsWithState = async (
  userId: string,
  state: ProposalState
) => {
  let newState = state;
  if (state === ProposalState.ACCEPTED_BOOKED) {
    newState = ProposalState.DEPOSIT_PAID;
  }
  const proposalQuery = query(
    collection(db, `${FIREBASE_COLLECTIONS.proposals}`),
    where("chefUid", "==", userId),
    where("proposalState", "==", newState)
  );
  const querySnapshot = await getDocs(proposalQuery);
  const proposedGigs = await Promise.all(
    querySnapshot.docs.map(async (proposal) => {
      const proposalDetails = proposal.data();
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.depositPaymentTime
      ) {
        proposalDetails.proposalHistory.depositPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.depositPaymentTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigCompletionTime
      ) {
        proposalDetails.proposalHistory.gigCompletionTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigCompletionTime
          );
      }
      if (
        proposalDetails.proposalHistory &&
        proposalDetails.proposalHistory.gigPaymentTime
      ) {
        proposalDetails.proposalHistory.gigPaymentTime =
          getIsoStringFromTimestamp(
            proposalDetails.proposalHistory.gigPaymentTime
          );
      }

      proposalDetails.createdAt =
        proposalDetails.createdAt &&
        getIsoStringFromTimestamp(proposalDetails.createdAt);
      proposalDetails.updatedAt =
        proposalDetails.updatedAt &&
        getIsoStringFromTimestamp(proposalDetails.updatedAt);

      const gigDoc = await getDoc(
        doc(db, `${FIREBASE_COLLECTIONS.gigs}/${proposalDetails.gigId}`)
      );
      if (gigDoc.exists()) {
        const gigDetails = gigDoc.data();
        gigDetails.gigId = gigDoc.id;
        gigDetails.createdAt =
          gigDetails.createdAt &&
          getIsoStringFromTimestamp(gigDetails.createdAt);
        return {
          gigDetails: gigDetails as UserProposedGig,
          proposalDetails: proposalDetails as GigProposal,
        };
      } else {
        throw new Error("Gig doesn't exist");
      }
    })
  );
  return proposedGigs;
};

/**
 * Check if a gig has expired based on its date.
 * For multiDate gigs, check the endDate; for single date gigs, check the date field.
 * Returns true if the gig's scheduled time has passed.
 */
const isGigExpired = (gig: Partial<Gig>): boolean => {
  const now = new Date();

  // For multiDate gigs, check the endDate
  if (gig.multiDate?.endDate) {
    const endDate = new Date(gig.multiDate.endDate);
    return endDate < now;
  }

  // For single date gigs, check the date field
  if (gig.date) {
    const gigDate = new Date(gig.date);
    return gigDate < now;
  }

  // If no date is set, don't filter out the gig
  return false;
};

const getGigs = async (
  paginationData: PaginationData,
  lastDocId: string | null,
  likedByUid: string | null,
  viewerUid?: string | null
) => {
  try {
    const { limit: limitNum, page } = paginationData;
    const skip = limitNum * (page - 1);
    let gigsData: Gig[];
    const lastDocSnapshot =
      lastDocId && (await getDoc(doc(db, `gigs/${lastDocId}`)));
    const snapShot = await getDocs(
      query(
        collection(db, "gigs"),
        orderBy("createdAt", "desc"),
        where("gigState", "in", DisplayGigsState),
        ...(likedByUid ? [where("likedBy", "array-contains", likedByUid)] : []),
        ...(lastDocId ? [startAfter(lastDocSnapshot)] : []),
        limit(limitNum)
      )
    );
    gigsData = (
      await Promise.all(
        snapShot.docs.map(async (doc) => {
          const data = doc.data();
          data.gigId = doc.id;
          data.createdAt = data.createdAt?.toDate().toISOString();
          return data as Gig;
        })
      )
    ).filter(
      (gig) =>
        canViewGig(gig, viewerUid ?? likedByUid ?? null) && !isGigExpired(gig)
    );
    return gigsData;
  } catch (error) {
    console.error(error);
  }
};

const getNearByGigs = async (
  lat: number,
  lng: number,
  viewerUid?: string | null
) => {
  const radiusInM = NEARBY_GIG_DISTANCE_LIMIT * 1000;
  const bounds = geohashQueryBounds([lat, lng], radiusInM);
  let snapShots: DocumentData[] = [];
  for (const bound of bounds) {
    const querySnapshot = await getDocs(
      query(
        collection(db, "gigs"),
        where("gigState", "in", DisplayGigsState),
        orderBy("geoHash"),
        startAt(bound[0]),
        endAt(bound[1]),
        limit(NUMBER_OF_NEARBY_GIG_LIMIT + POTENTIAL_FALSE_POSITIVES)
      )
    );
    snapShots = [...snapShots, ...querySnapshot.docs];
  }

  const filteredSnapShot: DocumentData[] = [];
  for (const doc of snapShots) {
    const address = doc.get("location") as GoogleMapResultData;
    const distanceInKm = distanceBetween(
      [address.lat, address.lng],
      [lat, lng]
    );
    if (distanceInKm * 1000 <= radiusInM) {
      filteredSnapShot.push(doc);
    }
  }

  return filteredSnapShot
    .slice(0, NUMBER_OF_NEARBY_GIG_LIMIT)
    .map((doc) => {
      const data = doc.data();
      data.gigId = doc.id;
      data.createdAt = data.createdAt.toDate().toISOString();
      return data as Gig;
    })
    .filter(
      (gig) =>
        canViewGig(gig, viewerUid ?? auth.currentUser?.uid ?? null) &&
        !isGigExpired(gig)
    );
};

const createGig = async (
  gigData: Gig,
  userUid: string,
  clientMeta?: { firstName?: string; lastName?: string }
) => {
  const sanitizedInvites = (gigData.invitedChefUids ?? [])
    .filter((id): id is string => Boolean(id))
    .slice(0, PrivateGigConstants.InviteLimit);
  const payload: Gig = {
    ...gigData,
    invitedChefUids: gigData.isPrivate ? sanitizedInvites : [],
    privateInviteLimit: gigData.isPrivate
      ? PrivateGigConstants.InviteLimit
      : undefined,
    isPrivate: Boolean(gigData.isPrivate),
  };
  const filteredData = removeUndefinedFields(payload);
  const docsRef = collection(db, "gigs");
  const q = query(
    docsRef,
    where("ownerUid", "==", userUid),
    where("gigState", "==", ProposalState.DRAFT)
  );
  const querySnapshot = await getDocs(q);
  const docRef = doc(db, `gigs/${querySnapshot.docs[0].id}`);

  await updateDoc(docRef, {
    gigState: ProposalState.NEW_GIG,
    invitedChefUids: filteredData.invitedChefUids ?? [],
    privateInviteLimit: filteredData.isPrivate
      ? filteredData.privateInviteLimit ?? PrivateGigConstants.InviteLimit
      : deleteField(),
    isPrivate: filteredData.isPrivate ?? false,
    gigId: docRef.id,
  });
  filteredData.location &&
    (await updateGeoHash(
      `gigs/${docRef.id}`,
      filteredData.location?.lat,
      filteredData.location?.lng
    ));

  await createUserLog(
    userUid,
    "Gig Created",
    `Gig created and published. Gig ID: ${docRef.id}`,
    { gigId: docRef.id, gigState: ProposalState.NEW_GIG },
    "Gig Creation"
  );
  if (filteredData.isPrivate && sanitizedInvites.length > 0) {
    const clientName = [clientMeta?.firstName, clientMeta?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const inviteBaseUrl =
      process.env.NEXT_PUBLIC_APP_URL || "https://app.morepleaze.com";

    await Promise.all(
      sanitizedInvites.map((chefUid) =>
        sendEmailRequest(chefUid, PRIVATE_GIG_INVITE_TEMPLATE_ID, [
          {
            key: "clientName",
            value: clientName || "A More Pleaze client",
          },
          {
            key: "gigTitle",
            value: filteredData.title || "a private gig",
          },
          {
            key: "ctaLink",
            value: `${inviteBaseUrl}/gig/${docRef.id}/proposal/${chefUid}`,
          },
        ])
      )
    );
  }

  return docRef.id;
};
const saveDraftGig = async (gigData: Gig, userUid: string) => {
  const sanitizedInvites = (gigData.invitedChefUids ?? [])
    .filter((id): id is string => Boolean(id))
    .slice(0, PrivateGigConstants.InviteLimit);
  const payload: Gig = {
    ...gigData,
    invitedChefUids: gigData.isPrivate ? sanitizedInvites : [],
    privateInviteLimit: gigData.isPrivate
      ? PrivateGigConstants.InviteLimit
      : undefined,
    isPrivate: Boolean(gigData.isPrivate),
  };
  const filteredData = removeUndefinedFields(payload);

  const uploadedData = {
    ...filteredData,
    ownerUid: userUid,
    createdAt: Timestamp.fromDate(new Date()),
    gigState: ProposalState.DRAFT,
  };

  const docsRef = collection(db, "gigs");
  const q = query(
    docsRef,
    where("ownerUid", "==", userUid),
    where("gigState", "==", ProposalState.DRAFT)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    const newDocRef = await addDoc(collection(db, "gigs"), uploadedData);
    await updateDoc(newDocRef, {
      gigId: newDocRef.id,
      privateInviteLimit: filteredData.isPrivate
        ? filteredData.privateInviteLimit ?? PrivateGigConstants.InviteLimit
        : PrivateGigConstants.InviteLimit,
      invitedChefUids: filteredData.invitedChefUids ?? [],
      isPrivate: filteredData.isPrivate ?? false,
    });
  } else {
    const docRef = doc(db, `gigs/${querySnapshot.docs[0].id}`);

    await updateDoc(docRef, {
      [filteredData.date ? "multiDate" : "date"]: deleteField(),
      ...uploadedData,
      gigId: docRef.id,
      privateInviteLimit: filteredData.isPrivate
        ? filteredData.privateInviteLimit ?? PrivateGigConstants.InviteLimit
        : PrivateGigConstants.InviteLimit,
      invitedChefUids: filteredData.invitedChefUids ?? [],
      isPrivate: filteredData.isPrivate ?? false,
    });
  }
  toast.info("Gig saved");
};

const fetchDraftGig = async (chefUid: string) => {
  const docsRef = collection(db, "gigs");
  const q = query(
    docsRef,
    where("ownerUid", "==", chefUid),
    where("gigState", "==", ProposalState.DRAFT)
  );
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return false;
  } else {
    const gigDoc = querySnapshot.docs[0];
    const gigData = gigDoc.data();
    gigData.gigId = gigDoc.id;
    gigData.createdAt = getIsoStringFromTimestamp(gigData.createdAt);
    gigData.invitedChefUids = gigData.invitedChefUids ?? [];
    gigData.privateInviteLimit =
      gigData.privateInviteLimit ?? PrivateGigConstants.InviteLimit;
    return gigData as Gig;
  }
};
const fetchGig = async (gigUid: string) => {
  const docsRef = collection(db, "gigs");
  const q = query(docsRef, where("gigId", "==", gigUid));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return {} as Gig;
  }
  const gigDoc = querySnapshot.docs[0];
  const gigData = gigDoc.data();
  gigData.gigId = gigDoc.id;
  gigData.createdAt = getIsoStringFromTimestamp(gigData.createdAt);
  gigData.invitedChefUids = gigData.invitedChefUids ?? [];
  gigData.privateInviteLimit =
    gigData.privateInviteLimit ?? PrivateGigConstants.InviteLimit;
  return gigData as Gig;
};

const acceptGig = async (
  gigProposalData: GigProposal,
  userUid: string,
  redirectUrl: string
) => {
  const docRef = doc(db, FIREBASE_COLLECTIONS.users, userUid);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const response = await fetchPostJSON("/api/users/accept-booking", {
      userId: userUid,
      proposalId: gigProposalData.id,
      redirectUrl,
    });
    return response;
  }
};

const updateGig = async (gigData: Gig) => {
  const filteredData = removeUndefinedFields(gigData);
  if (filteredData.gigId) {
    const gigRef = doc(db, FIREBASE_COLLECTIONS.gigs, filteredData.gigId);
    console.log(gigRef);
    const docSnap = await getDoc(gigRef);
    const latestGigData = docSnap.data() as Gig;
    if (!docSnap.exists()) {
      toast.error("Gig not found.");
      return;
    }

    // Block if gig is not editable by basic guard
    if (!canEditGig(latestGigData)) {
      toast.error(`You can't edit this gig after selecting a proposal.`);
      return;
    }

    // Block if any proposals for this gig are in selected/active states
    const anySelected = await hasSelectedProposals(filteredData.gigId);
    if (anySelected) {
      toast.error("Gig cannot be edited because a proposal has been selected.");
      return;
    }

    await updateDoc(gigRef, {
      ...filteredData,
    });
    filteredData.location &&
      (await updateGeoHash(
        `gigs/${gigRef.id}`,
        filteredData.location?.lat,
        filteredData.location?.lng
      ));

    // Log gig update
    if (latestGigData.ownerUid) {
      await createUserLog(
        latestGigData.ownerUid,
        "Gig Updated",
        `Gig ${filteredData.gigId} was updated`,
        { gigId: filteredData.gigId },
        "Gig Update"
      );
    }

    toast.success("Gig updated successfully.");
  }
};

/**
 * Marks a chef's final acceptance of a gig after client selection.
 * This is a tracking field used for the 48-hour pending period.
 * @param proposalId The ID of the proposal to update
 */
const markChefFinalAcceptance = async (proposalId: string) => {
  if (!proposalId) {
    return;
  }
  const docRef = doc(db, FIREBASE_COLLECTIONS.proposals, proposalId);
  await updateDoc(docRef, {
    chefAcceptedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export {
  acceptGig,
  countAllProposals,
  countGigsByState,
  countLikedGigs,
  countLikedGigsByState,
  countProposalsForChefByState,
  countProposalsForClientByState,
  createGig,
  hasSelectedProposals,
  fetchAcceptedProposalsForClientForStates,
  fetchAcceptedProposalsForClientWithState,
  fetchAllProposals,
  fetchAllProposalsWithState,
  fetchClientGigs,
  fetchClientGigsWithState,
  fetchDraftGig,
  fetchDraftProposal,
  fetchGig,
  fetchGigProposal,
  fetchGigsDataByUids,
  fetchGigsForPublicProfile,
  fetchLikedGigsForChef,
  fetchLikedGigsWithState,
  fetchLikedProposedGigsWithState,
  fetchProposals,
  fetchProposedGigs,
  fetchProposedGigsForMultipleStates,
  fetchProposedGigsWithState,
  fetchRejectedProposals,
  fetchTotalProposals,
  getGigs,
  getNearByGigs,
  handleBookGigProposal,
  handleBookGigProposalFinalPayment,
  handleCancelGigProposal,
  handleCancelGigProposalChefAtFaultCancellation,
  handleCancelGigProposalChefGracePeriodCancellation,
  handleCancelGigProposalClientAtFault,
  handleCancelGigProposalClientGracePeriodCancellation,
  handleCancelProposal,
  handleCancelProposalAfterSubmit,
  handleDeclinedGigProposal,
  handleGigCompletionFlow,
  saveDraftGig,
  saveGigProposal,
  submitGigProposal,
  updateGig,
  updateGigProposal,
  updateGigState,
  markChefFinalAcceptance,
};
